// Full field map mirroring the "Client Intake + Career Discovery Assessment" PDF.
// Each step has fields; field.type drives the renderer.
// Types: text, email, tel, url, textarea, select, checks (multi checkbox), scale10, info
// field.required = always required. field.requiredIf = conditionally required.
//   requiredIf: { field: "<name>", includes: "<value>" }  (required if that checks-field includes value)
//   requiredIf: { field: "<name>", notEmpty: true }        (required if that field has any value)
// field.tip = Smart Tip explanatory text shown via the info icon.

export const DIMENSIONS = [
  { key: "analytical", label: "Analytical / Investigative" },
  { key: "creative", label: "Creative / Communication" },
  { key: "people", label: "People / Helping" },
  { key: "business", label: "Business / Leadership" },
  { key: "technology", label: "Technology / Systems" },
  { key: "organization", label: "Organization / Execution" },
];

// 36 statements, 6 per dimension, in the same order as DIMENSIONS.
export const APTITUDE = [
  "I enjoy understanding why something works or fails.",
  "I enjoy puzzles, data, patterns or research.",
  "I ask questions before accepting an answer.",
  "I like evidence and logic when making decisions.",
  "I enjoy troubleshooting complex problems.",
  "I can stay focused on a difficult problem.",
  "I enjoy writing, storytelling, design or content.",
  "I can explain complex ideas simply.",
  "I notice how words and visuals affect people.",
  "I enjoy brainstorming different ways to communicate.",
  "I enjoy presenting, speaking or performing.",
  "I like creative work with room for originality.",
  "I enjoy helping others learn or solve problems.",
  "I listen to understand different perspectives.",
  "I enjoy mentoring or coaching.",
  "I enjoy meeting and building relationships.",
  "I want my work to have positive impact on people.",
  "I enjoy facilitating collaboration.",
  "I like taking ownership for outcomes.",
  "I enjoy planning, prioritizing and deciding.",
  "I am comfortable persuading others.",
  "I enjoy thinking about customers, markets or growth.",
  "I am willing to take calculated risks.",
  "I enjoy leading projects or coordinating people.",
  "I enjoy learning software, tools or technology.",
  "I like understanding systems and processes.",
  "I enjoy improving inefficient workflows.",
  "I learn well through experimentation.",
  "I enjoy building, configuring or optimizing digital solutions.",
  "I am curious about how technology changes work.",
  "I enjoy turning goals into step-by-step plans.",
  "I pay attention to deadlines and details.",
  "I enjoy coordinating multiple tasks.",
  "People can rely on me to follow through.",
  "I like creating structure when things are unclear.",
  "I get satisfaction from completing and improving processes.",
];

export const APTITUDE_LEGEND = [
  "1 = Not like me at all",
  "2 = Slightly",
  "3 = Sometimes",
  "4 = Very much",
  "5 = Extremely",
];

export const INTERVIEW = [
  "I can introduce myself confidently without sounding memorized.",
  "I can explain achievements with evidence.",
  "I can answer behavioral questions clearly.",
  "I can explain why I want the target role or company.",
  "I can handle difficult interview questions.",
  "I communicate professionally in meetings.",
  "I can discuss compensation professionally.",
  "I present confidently on video.",
  "I know how to follow up after interviews or networking.",
];

export const PERSONAS = [
  { key: "student", label: "Student or early-career explorer",
    hint: "School or college students, recent graduates, limited professional experience." },
  { key: "junior", label: "Junior professional seeking growth",
    hint: "Working professionals seeking positioning, interviews, promotion or a move." },
  { key: "international", label: "International study or work aspirant",
    hint: "Planning education, employment or career development in another country." },
];

export const CAREER_EXPLORATION = [
  { dim: "Analytical / Investigative", families: "Data, research, finance, strategy, engineering, business analysis", validate: "Test sustained analytical work and subject interest." },
  { dim: "Creative / Communication", families: "Marketing, content, communications, UX, media, education", validate: "Create and refine real outputs." },
  { dim: "People / Helping", families: "L&D, HR, coaching, education, customer success, consulting", validate: "Try mentoring, facilitation or service work." },
  { dim: "Business / Leadership", families: "Consulting, product, sales, entrepreneurship, management, operations", validate: "Own a measurable project or outcome." },
  { dim: "Technology / Systems", families: "Technology, product, DAP, implementation, automation, systems", validate: "Build or configure a tool or improve a workflow." },
  { dim: "Organization / Execution", families: "Project and program management, operations, PMO, implementation", validate: "Plan and deliver a real project." },
];

// Smart Tips for the two Likert steps and the acknowledgment (shown at step level).
export const INTERVIEW_TIP =
  "Rate your confidence. 1 means you completely struggle with this, and 5 means you are highly confident.";
export const APTITUDE_TIP =
  "Rate each statement based on who you are today, not who you want to be. 1 = Not like me at all; 5 = Extremely like me. Go with your gut reaction.";
export const PERSONA_TIP =
  "Choose the profile that best fits you so we only show the questions that are relevant to your journey.";
export const REFLECTION_TIP =
  "Summarize your findings and turn your top dimensions into hypotheses you will test.";
export const ACK_TIP =
  "Read the acknowledgment, digitally sign your name, and date the document to complete your intake.";

// Universal + persona + discovery + reflection + acknowledgement steps
export const STEPS = [
  {
    id: "profile",
    title: "Universal profile",
    subtitle: "A. Tell us who you are.",
    fields: [
      { name: "full_name", label: "Full name", type: "text", required: true,
        tip: "Enter your legal name and the name you'd like Rhea to use during your session." },
      { name: "preferred_name", label: "Preferred name", type: "text",
        tip: "Enter your legal name and the name you'd like Rhea to use during your session." },
      { name: "age_range", label: "Age or age range", type: "text", required: true,
        tip: "Share your age or an age range. It helps Rhea tailor advice to your stage." },
      { name: "email", label: "Email", type: "email", required: true,
        tip: "Provide your current details to ensure seamless scheduling." },
      { name: "phone", label: "Phone or WhatsApp", type: "tel", required: true,
        tip: "Provide your current details to ensure seamless scheduling." },
      { name: "location", label: "Current city and country", type: "text", required: true,
        tip: "Provide your current details to ensure seamless scheduling." },
      { name: "timezone", label: "Preferred consultation timezone", type: "text", required: true,
        tip: "Provide your current details to ensure seamless scheduling." },
      { name: "linkedin", label: "LinkedIn URL (if applicable)", type: "url",
        tip: "Paste the direct link to your profile so Rhea can audit your digital presence." },
      { name: "current_title", label: "Current education or job title", type: "text", required: true,
        tip: "Tell us your current role or program so Rhea understands your starting point." },
      { name: "referral", label: "How did you hear about Rhea?", type: "text", required: true,
        tip: "Let us know how you found us, for example LinkedIn, a referral or a search." },
      { name: "priorities", label: "Top priorities", type: "checks", required: true, options: [
        "Career direction", "Resume / CV", "Interview preparation", "Career transition",
        "Study abroad", "Application strategy", "College / degree choice", "LinkedIn positioning",
        "Professional grooming", "Promotion / growth", "Work abroad", "Confidence / presentation", "Other",
      ], tip: "Select the primary goals you want to tackle first to allocate our time effectively." },
      { name: "priorities_other", label: "Other priority", type: "text",
        requiredIf: { field: "priorities", includes: "Other" },
        tip: "You selected Other above. Describe the priority in a few words." },
      { name: "success", label: "What would make this consultation successful for you?", type: "textarea", required: true,
        tip: "Define your ideal outcome. What exact clarity or strategy do you want to walk away with?" },
    ],
  },
  {
    id: "persona-select",
    title: "Which describes you?",
    subtitle: "Choose all that apply. We will only ask questions relevant to you.",
    personaSelect: true,
    tip: PERSONA_TIP,
  },
  {
    id: "student",
    persona: "student",
    title: "Students and early-career explorers",
    subtitle: "Complete if this applies to you.",
    fields: [
      { name: "s_institution", label: "School / college / university + program", type: "text", required: true,
        tip: "Name your institution and the program or major you are enrolled in." },
      { name: "s_year", label: "Year / semester + expected graduation", type: "text", required: true,
        tip: "For example, 2nd year, expected to graduate in 2027." },
      { name: "s_subjects_enjoy", label: "Subjects you enjoy most and why", type: "textarea", required: true,
        tip: "Think about the classes that energize you and the reason they hold your attention." },
      { name: "s_subjects_dislike", label: "Subjects you dislike or find difficult", type: "textarea", required: true,
        tip: "Being honest here helps Rhea steer you toward strengths and away from poor-fit paths." },
      { name: "s_academic", label: "Academic standing (optional)", type: "text",
        tip: "Optional. Share your GPA, percentage or rank only if you are comfortable." },
      { name: "s_experience", label: "Projects, internships, certifications, volunteering or extracurriculars", type: "textarea", required: true,
        tip: "List anything beyond coursework that shows initiative or skill. Quantify impact where you can." },
      { name: "s_considering", label: "Careers currently considering", type: "text", required: true,
        tip: "Name the paths on your radar right now, even if you are unsure." },
      { name: "s_ruled_out", label: "Careers ruled out", type: "text", required: true,
        tip: "Knowing what you have already crossed off saves us time and narrows the focus." },
      { name: "s_day_to_day", label: "What kind of work would you enjoy day to day?", type: "textarea", required: true,
        tip: "Picture an ideal workday. What tasks would you happily repeat?" },
      { name: "s_strong_skills", label: "Strongest skills today", type: "text", required: true,
        tip: "Identify your current strengths and the specific hard or soft skills you rely on." },
      { name: "s_develop_skills", label: "Skills you want to develop", type: "text", required: true,
        tip: "Identify the specific hard or soft skills you need to build next." },
      { name: "s_matters", label: "What matters most in a future career?", type: "textarea", required: true,
        tip: "Think about your values, for example impact, income, flexibility or growth." },
      { name: "s_family", label: "Family expectations or constraints around career choice", type: "textarea", required: true,
        tip: "Be honest about any expectations or constraints so we can plan realistically." },
      { name: "s_study_abroad", label: "Study-abroad goals", type: "checks", options: [
        "USA", "UK", "Canada", "Europe", "Australia / New Zealand", "Middle East", "Other",
      ], tip: "Select destinations you are considering. Leave blank if study abroad is not a goal." },
      { name: "s_intake", label: "Target intake / year", type: "text",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "Since you picked a destination, tell us the intake or year you are targeting." },
      { name: "s_degree", label: "Target degree / field", type: "text",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "Be as specific as possible about the degree or field you want to pursue." },
      { name: "s_budget", label: "Approximate budget + funding source", type: "text",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "Be as specific as possible about where you want to go and your financial constraints." },
      { name: "s_scholarships", label: "Scholarships / financial aid explored", type: "text",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "List any scholarships or aid you have looked into, or note if you have not started." },
      { name: "s_tests", label: "Tests / credentials completed", type: "text",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "For example IELTS, TOEFL, GRE, GMAT or SAT, with scores if available." },
      { name: "s_why_abroad", label: "Why do you want to study abroad?", type: "textarea",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "Share the real motivation behind studying overseas." },
      { name: "s_choose_country", label: "What would make you choose one country over another?", type: "textarea",
        requiredIf: { field: "s_study_abroad", notEmpty: true },
        tip: "Think about cost, career outcomes, lifestyle or immigration pathways." },
    ],
  },
  {
    id: "junior",
    persona: "junior",
    title: "Junior professionals and career growth",
    subtitle: "Complete if this applies to you.",
    fields: [
      { name: "j_company", label: "Current company, role and industry", type: "text", required: true,
        tip: "Tell us where you work, your title and the industry you operate in." },
      { name: "j_experience", label: "Total experience", type: "text", required: true,
        tip: "State your total years of professional experience." },
      { name: "j_previous", label: "Previous roles / industries", type: "text", required: true,
        tip: "List earlier roles or industries so Rhea sees your trajectory." },
      { name: "j_responsibilities", label: "Current responsibilities", type: "textarea", required: true,
        tip: "Describe what you actually own and do day to day." },
      { name: "j_achievements", label: "Three achievements you are proud of (quantify if possible)", type: "textarea", required: true,
        tip: "List 3 things you are proud of. Smart Tip: use numbers, for example led a team of 5 or increased sales by 20 percent, to show impact." },
      { name: "j_skills_use", label: "Skills you use most", type: "text", required: true,
        tip: "Identify your current strengths, the hard or soft skills you lean on daily." },
      { name: "j_skills_build", label: "Skills you want to build", type: "text", required: true,
        tip: "Identify the specific skills you need to develop for your next move." },
      { name: "j_enjoy", label: "What you enjoy most about current work", type: "textarea", required: true,
        tip: "Note the parts of your job that energize you." },
      { name: "j_frustrate", label: "What frustrates or drains you", type: "textarea", required: true,
        tip: "Be candid about what wears you down. It signals what to change." },
      { name: "j_target_role", label: "Target role / level / function", type: "text", required: true,
        tip: "Be as specific as possible about where you want to go." },
      { name: "j_target_companies", label: "Target companies / industries", type: "text", required: true,
        tip: "Name the companies or industries you are aiming for." },
      { name: "j_target_comp", label: "Target compensation (optional)", type: "text",
        tip: "Optional. Share your compensation goal if you are comfortable." },
      { name: "j_blocker", label: "What is preventing the move?", type: "textarea", required: true,
        tip: "Be honest about your roadblocks, whether it is confidence, missing skills or timing." },
      { name: "j_interview_conf", label: "Interview confidence", type: "scale10", required: true,
        tip: "Rate from 1 (very low) to 10 (very high) how confident you feel in interviews." },
      { name: "j_comm_conf", label: "Professional communication confidence", type: "scale10", required: true,
        tip: "Rate from 1 to 10 how confident you feel communicating at work." },
      { name: "j_positioning", label: "Positioning check (tick what is true today)", type: "checks", options: [
        "Resume communicates my value",
        "LinkedIn matches target roles",
        "I can tell my career story in 60-90 seconds",
        "I can quantify achievements",
        "I have a focused target-role strategy",
        "I know my skill gaps",
        "I have a repeatable interview-prep process",
      ], tip: "Tick only the statements that are genuinely true for you today. It is fine to leave some unticked." },
    ],
  },
  {
    id: "international",
    persona: "international",
    title: "International study, work and career mobility",
    subtitle: "Complete if this applies to you. Guidance is strategic and is not immigration or legal advice.",
    fields: [
      { name: "i_target", label: "Target country / region", type: "text", required: true,
        tip: "Be as specific as possible about where you want to go." },
      { name: "i_goal", label: "Goal", type: "select", required: true, options: ["Study", "Work", "Both"],
        tip: "Tell us whether you are moving to study, to work, or both." },
      { name: "i_timeline", label: "Target timeline / intake", type: "text", required: true,
        tip: "State when you plan to move or the intake you are targeting." },
      { name: "i_current_status", label: "Current country + status", type: "text", required: true,
        tip: "Share where you live now and your current visa or residency status." },
      { name: "i_qualification", label: "Highest qualification + field", type: "text", required: true,
        tip: "State your highest completed qualification and its field." },
      { name: "i_experience", label: "Professional experience", type: "text", required: true,
        tip: "Summarize your relevant work experience." },
      { name: "i_target_degree", label: "Target degree / role / industry", type: "text", required: true,
        tip: "Be as specific as possible about the degree, role or industry you want." },
      { name: "i_why", label: "Why this country / region?", type: "textarea", required: true,
        tip: "Share what draws you to this destination." },
      { name: "i_backup", label: "Backup countries", type: "text", required: true,
        tip: "List alternative destinations in case the first choice does not work out." },
      { name: "i_budget", label: "Approximate tuition + living budget", type: "text", required: true,
        tip: "Be as specific as possible about your financial constraints." },
      { name: "i_funding", label: "Funding / scholarship requirement", type: "text", required: true,
        tip: "Note whether you need funding or scholarships to make the move." },
      { name: "i_tests", label: "Tests or credentials completed", type: "text", required: true,
        tip: "For example IELTS, TOEFL, GRE or GMAT, with scores if available." },
      { name: "i_progress", label: "Application progress", type: "text", required: true,
        tip: "Where are you in the process, from research to submitted applications?" },
      { name: "i_concern", label: "Biggest concern", type: "text", required: true,
        tip: "Be honest about your biggest roadblock, whether it is visas, cost or fit." },
      { name: "i_need", label: "What do you need most: country comparison, program selection, career direction, profile positioning, applications, interviews, networking or job-search strategy?", type: "textarea", required: true,
        tip: "Pick the one or two areas where our time together will help you the most." },
    ],
  },
  {
    id: "discovery",
    title: "Career discovery",
    subtitle: "B. A few open reflections.",
    fields: [
      { name: "d_good_at", label: "What are you naturally good at?", type: "textarea", required: true,
        tip: "Think about tasks that feel easy to you but hard to others." },
      { name: "d_help_with", label: "What do people ask you for help with?", type: "textarea", required: true,
        tip: "The help others seek from you is a strong clue to your natural strengths." },
      { name: "d_problems", label: "What problems do you enjoy solving?", type: "textarea", required: true,
        tip: "Think about tasks that feel easy to you but hard to others." },
      { name: "d_prefer", label: "Do you prefer working with...", type: "checks", required: true, options: [
        "People", "Data / information", "Ideas", "Technology", "Physical things", "A mix",
      ], tip: "Reflect on your ideal workday and select what you gravitate toward." },
      { name: "d_structure", label: "Structure and processes, or ambiguity and experimentation?", type: "text", required: true,
        tip: "Do you prefer clear instructions, or open-ended creative problem solving?" },
      { name: "d_ownership", label: "Individual ownership, teamwork, leadership, or a mix?", type: "text", required: true,
        tip: "Reflect on how you like to contribute, alone, in a team or leading." },
      { name: "d_environment", label: "What environment brings out your best?", type: "textarea", required: true,
        tip: "Consider pace, culture, autonomy and setting." },
      { name: "d_nonneg", label: "What are your non-negotiables?", type: "textarea", required: true,
        tip: "What must your future career have? For example remote work, a specific salary or no weekend work." },
      { name: "d_no_constraints", label: "If money and fear were not constraints, what would you explore?", type: "textarea", required: true,
        tip: "Dream freely. This reveals interests you may be suppressing." },
      { name: "d_avoiding", label: "What career decision are you currently avoiding?", type: "textarea", required: true,
        tip: "Naming the decision you keep postponing is often the fastest way forward." },
    ],
  },
  {
    id: "interview",
    title: "Interview and professional grooming",
    subtitle: "C. Rate each statement from 1 (not at all) to 5 (completely true).",
    interview: true,
    tip: INTERVIEW_TIP,
  },
  {
    id: "aptitude",
    title: "Career discovery and aptitude screen",
    subtitle: "Part II. Rate how much each statement is like you.",
    aptitude: true,
    tip: APTITUDE_TIP,
  },
  {
    id: "reflection",
    title: "Reflection",
    subtitle: "Your top dimensions are suggested from your ratings. Use them as hypotheses to test.",
    reflection: true,
    tip: REFLECTION_TIP,
    fields: [
      { name: "r_top2", label: "My top 2 dimensions are", type: "text", required: true,
        tip: "Summarize the two dimensions that scored highest for you." },
      { name: "r_investigate", label: "Career fields I want to investigate", type: "textarea", required: true,
        tip: "List the fields your results suggest are worth exploring." },
      { name: "r_never_considered", label: "One career I have never seriously considered", type: "textarea", required: true,
        tip: "Name a path you have overlooked. Fresh options often hide here." },
      { name: "r_skill_90", label: "One skill to build in 90 days", type: "textarea", required: true,
        tip: "Pick a single, specific skill you can make progress on within three months." },
      { name: "r_experience", label: "One project, internship or experience to seek", type: "textarea", required: true,
        tip: "Choose one real experience that would test your top direction." },
      { name: "r_assumption", label: "One assumption about my career I need to test", type: "textarea", required: true,
        tip: "Identify a belief you are treating as fact that deserves a reality check." },
    ],
  },
  {
    id: "acknowledgement",
    title: "Acknowledgment",
    acknowledgement: true,
    tip: ACK_TIP,
    fields: [
      { name: "ack_name", label: "Client name", type: "text", required: true,
        tip: "Type your full name to confirm this is your submission." },
      { name: "ack_signature", label: "Signature (type your full name)", type: "text", required: true,
        tip: "Digitally sign by typing your full name exactly." },
      { name: "ack_date", label: "Date", type: "date", required: true,
        tip: "Confirm today's date to complete your intake." },
    ],
  },
];

export const ACK_TEXT =
  "I understand that the career discovery assessment is an informal educational self assessment. " +
  "It does not guarantee admission, employment, salary, visa approval or a specific career outcome. " +
  "International study or work guidance is strategic and does not constitute immigration or legal advice.";

export const INTRO_NOTE =
  "The career discovery test is an informal self reflection and aptitude screening exercise, not a clinical, " +
  "IQ, diagnostic or licensed psychometric assessment. Results are one input alongside interests, academic " +
  "performance, skills, values, experience, finances and career research.";
